import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { ConfirmModal } from "../../ConfirmModal";
import {
  SearchableDropdown,
  RowButton,
  ManageDropdownOpener,
} from "../../SearchableDropdown";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { PopoverManageRow } from "../../PopoverManageRow";
import {
  OidcProviderModal,
  type OidcProviderValues,
} from "./OidcProviderModal";

export function IdentityProvidersButton() {
  const z = useZero();
  const [providers] = useQuery(queries.oidcProviders());
  const [showRegister, setShowRegister] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const register = (values: OidcProviderValues) => {
    z.mutate(
      sharedMutators.registerOidcProvider({ id: nanoid(12), ...values })
    );
  };

  const editing = providers.find((p) => p.id === editId);

  const saveEdit = (values: OidcProviderValues) => {
    if (!editId) return;
    z.mutate(
      sharedMutators.updateOidcProvider({
        id: editId,
        ...values,
        jwksUri: values.jwksUri ?? null,
      })
    );
  };

  return (
    <>
      <SearchableDropdown
        aria-label="Identity providers"
        searchPlaceholder="Search identity providers…"
        opener={
          <Tooltip content="Identity providers">
            <ManageDropdownOpener>
              <Icon name="id-card-lanyard" size={16} />
            </ManageDropdownOpener>
          </Tooltip>
        }
      >
        {(query, close) => {
          const needle = query.trim().toLowerCase();
          const filtered = needle
            ? providers.filter(
                (p) =>
                  p.name.toLowerCase().includes(needle) ||
                  p.issuer.toLowerCase().includes(needle)
              )
            : providers;
          return (
            <>
              {filtered.length === 0 ? (
                <div style={{ padding: "8px 8px 12px" }}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {needle ? "No matches." : "No identity providers yet."}
                  </Text>
                </div>
              ) : (
                filtered.map((p) => (
                  <PopoverManageRow
                    key={p.id}
                    icon="id-card-lanyard"
                    title={p.name}
                    subtitle={p.issuer}
                    dimmed={!!p.disabled}
                    menuItems={[
                      {
                        left: <Icon name="pencil" size={16} />,
                        label: "Edit",
                        onClick: () => {
                          close();
                          setEditId(p.id);
                        },
                      },
                      {
                        left: (
                          <Icon
                            name={p.disabled ? "check" : "circle-off"}
                            size={16}
                          />
                        ),
                        label: p.disabled ? "Enable" : "Disable",
                        onClick: () =>
                          z.mutate(
                            sharedMutators.updateOidcProvider({
                              id: p.id,
                              disabled: !p.disabled,
                            })
                          ),
                      },
                      {
                        left: <Icon name="trash" size={16} />,
                        label: "Delete",
                        onClick: () => {
                          close();
                          setConfirmDelete({ id: p.id, name: p.name });
                        },
                      },
                    ]}
                  />
                ))
              )}
              <RowButton
                onClick={() => {
                  close();
                  setShowRegister(true);
                }}
              >
                <Icon name="plus" size={16} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text variant="text-regular" color="var(--text-primary)">
                    Add identity provider
                  </Text>
                  <Text
                    ellipsis
                    variant="text-small"
                    color="var(--text-secondary)"
                  >
                    Trust an OIDC issuer like GitHub Actions
                  </Text>
                </div>
              </RowButton>
            </>
          );
        }}
      </SearchableDropdown>

      {showRegister && (
        <OidcProviderModal
          onClose={() => setShowRegister(false)}
          onSubmit={register}
        />
      )}
      {editing && (
        <OidcProviderModal
          initial={{
            name: editing.name,
            issuer: editing.issuer,
            audience: editing.audience,
            jwksUri: editing.jwksUri ?? undefined,
          }}
          onClose={() => setEditId(null)}
          onSubmit={saveEdit}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            z.mutate(
              sharedMutators.deleteOidcProvider({ id: confirmDelete.id })
            );
            setConfirmDelete(null);
          }}
          title="Delete identity provider"
          description={`Removing “${confirmDelete.name}” immediately breaks every service account federation that uses it. This can’t be undone.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </>
  );
}
